import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Checkbox,
  Input,
  Button,
  Tag,
  message,
  Spin,
  Empty,
  Alert,
} from "antd";
import { PlusOutlined, CloseOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useAuth } from "../../contexts/AuthContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const NotificationContainer = styled.div`
  background-color: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 15px;
`;

const SectionSubtitle = styled.h6`
  font-weight: 600;
  color: #3c2f61;
  margin-bottom: 10px;
`;

const MemberList = styled.div`
  max-height: 250px;
  overflow-y: auto;
  margin-bottom: 15px;
  padding: 10px;
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
`;

const MemberItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const MemberInfo = styled.div`
  margin-left: 10px;
  flex: 1;
`;

const MemberName = styled.span`
  font-weight: 500;
  color: #333;
`;

const MemberEmail = styled.span`
  color: #666;
  font-size: 13px;
  margin-left: 8px;
`;

const EmailInputContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
`;

const EmailTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`;

const StyledTag = styled(Tag)`
  padding: 5px 10px;
  font-size: 13px;
`;

const NotificationSettings = ({
  selectedMemberEmails,
  setSelectedMemberEmails,
  additionalEmails,
  setAdditionalEmails,
}) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [newEmail, setNewEmail] = useState("");

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_URL}/companies/members/notification-list`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setMembers(response.data.data || []);
    } catch (error) {
      console.error("Error fetching members:", error);
      message.error("Failed to fetch members list.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedMemberEmails(["all"]);
    } else {
      setSelectedMemberEmails([]);
    }
  };

  const handleMemberSelect = (email, checked) => {
    if (selectAll) {
      // If "Select All" was checked, uncheck it and start fresh
      setSelectAll(false);
      setSelectedMemberEmails([email]);
    } else {
      if (checked) {
        setSelectedMemberEmails([...selectedMemberEmails, email]);
      } else {
        setSelectedMemberEmails(selectedMemberEmails.filter(e => e !== email));
      }
    }
  };

  const validateEmail = (email) => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  };

  const handleAddEmail = () => {
    const trimmedEmail = newEmail.trim();
    
    if (!trimmedEmail) {
      message.warning("Please enter an email address.");
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      message.error("Please enter a valid email address.");
      return;
    }

    if (additionalEmails.includes(trimmedEmail)) {
      message.warning("This email has already been added.");
      return;
    }

    // Check if email belongs to a member
    const isMember = members.some(m => m.user_email === trimmedEmail);
    if (isMember) {
      message.warning("This email belongs to a member. Please select them from the members list.");
      return;
    }

    setAdditionalEmails([...additionalEmails, trimmedEmail]);
    setNewEmail("");
    message.success("Email added successfully.");
  };

  const handleRemoveEmail = (emailToRemove) => {
    setAdditionalEmails(additionalEmails.filter(email => email !== emailToRemove));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  return (
    <NotificationContainer>
      <Alert
        message="Choose who to notify about this event"
        type="info"
        showIcon
        style={{ marginBottom: "15px" }}
      />

      <SectionSubtitle>Company Members</SectionSubtitle>
      
      <Spin spinning={loading}>
        {members.length > 0 ? (
          <>
            <div style={{ marginBottom: "10px" }}>
              <Checkbox
                checked={selectAll}
                onChange={(e) => handleSelectAll(e.target.checked)}
              >
                <strong>Select All Members</strong>
              </Checkbox>
            </div>

            <MemberList>
              {members.map((member) => (
                <MemberItem key={member.id}>
                  <Checkbox
                    checked={selectAll || selectedMemberEmails.includes(member.user_email)}
                    disabled={selectAll}
                    onChange={(e) => handleMemberSelect(member.user_email, e.target.checked)}
                  />
                  <MemberInfo>
                    <MemberName>
                      {member.first_name} {member.last_name}
                    </MemberName>
                    <MemberEmail>{member.user_email}</MemberEmail>
                  </MemberInfo>
                </MemberItem>
              ))}
            </MemberList>

            <div style={{ fontSize: "13px", color: "#666", marginTop: "5px" }}>
              {selectAll 
                ? `All ${members.length} members will be notified`
                : `${selectedMemberEmails.length} member(s) selected`
              }
            </div>
          </>
        ) : (
          <Empty description="No members found" />
        )}
      </Spin>

      <div style={{ marginTop: "20px" }}>
        <SectionSubtitle>Additional Email Addresses (Optional)</SectionSubtitle>
        <p style={{ fontSize: "13px", color: "#666", marginBottom: "10px" }}>
          Add email addresses of people who are not members
        </p>

        <EmailInputContainer>
          <Input
            placeholder="Enter email address"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddEmail}
            style={{ backgroundColor: "#3c2f61", borderColor: "#3c2f61" }}
          >
            Add
          </Button>
        </EmailInputContainer>

        {additionalEmails.length > 0 && (
          <EmailTagsContainer>
            {additionalEmails.map((email, index) => (
              <StyledTag
                key={index}
                closable
                onClose={() => handleRemoveEmail(email)}
                closeIcon={<CloseOutlined />}
              >
                {email}
              </StyledTag>
            ))}
          </EmailTagsContainer>
        )}
      </div>
    </NotificationContainer>
  );
};

export default NotificationSettings;